import { LocalCliError } from "./errors.js";

export const OPEN_TARGETS = ["webapp", "foundry", "portal"] as const;
export type OpenTarget = (typeof OPEN_TARGETS)[number];

/** The Microsoft Foundry portal. The new portal switches projects in-UI; there
 *  is no documented stable per-project deep link, so we open the portal home. */
export const FOUNDRY_PORTAL_URL = "https://ai.azure.com";

/** The minimal gateway facts `resolveOpenUrl` needs (subset of GatewayContext). */
export interface GatewayLocator {
  gatewayUrl: string;
  gatewayTenantId: string;
  subscriptionId: string;
  containerAppResourceId: string;
}

/** Azure portal deep link to a resource group's overview blade. */
export function azurePortalRgUrl(
  tenantId: string,
  subscriptionId: string,
  resourceGroup: string,
): string {
  return `https://portal.azure.com/#@${tenantId}/resource/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/overview`;
}

/** Parse subscriptionId + resourceGroup out of an ARM resource id. */
export function parseSubAndRg(
  armId: string,
): { subscriptionId: string; resourceGroup: string } | null {
  const subscriptionId = (/\/subscriptions\/([^/]+)/i.exec(armId))?.[1];
  const resourceGroup = (/\/resourceGroups\/([^/]+)/i.exec(armId))?.[1];
  if (!subscriptionId || !resourceGroup) return null;
  return { subscriptionId, resourceGroup };
}

/**
 * Resolve the URL to open for a target. `foundry` needs no gateway; `webapp` and
 * `portal` require the resolved gateway locator. Pure — the caller supplies the
 * gateway (or null) so this is fully unit-testable.
 */
export function resolveOpenUrl(target: OpenTarget, gw: GatewayLocator | null): string {
  if (target === "foundry") return FOUNDRY_PORTAL_URL;
  if (!gw) {
    throw new LocalCliError({
      code: "OPEN_NO_GATEWAY",
      message: `Cannot resolve the '${target}' URL — no m8t gateway found in the active subscription.`,
      hint: "Sign in with 'az login' and check 'az account show', then retry. (webapp/portal need a deployed gateway.)",
    });
  }
  if (target === "webapp") return gw.gatewayUrl;
  // portal → the resource group's blade in the Azure portal
  const parsed = parseSubAndRg(gw.containerAppResourceId);
  if (!parsed) {
    throw new LocalCliError({
      code: "OPEN_BAD_RESOURCE_ID",
      message: `Could not parse subscription/resource-group from the gateway resource id: ${gw.containerAppResourceId}`,
    });
  }
  return azurePortalRgUrl(gw.gatewayTenantId, parsed.subscriptionId, parsed.resourceGroup);
}
